import i18next from 'i18next'
import type {
  EnhancedProductFamily,
  EnhancedProductFamilyId,
  EnhancedProductGroup,
  EnhancedProductGroupId,
  EnhancedProductVariant,
  EnhancedProductVariantId,
} from '#api/enhancements/enhancedTmsApiTypes'
import { store } from '#app/store'
import type { ConfigurationMode } from '#features/Configuration/ApiConfiguration'
import type { FavoriteEntryId } from '#features/FavoriteSets/FavoriteSet'
import type { Tg3ConversionInsightsLookup } from '#features/LegacyTg3Code/mapTg3ConversionInsights'
import { enrichWithNationalCodes } from '#features/NationalCodes/enrichWithNationalCodes'
import { calculateDeltaPriceRange, type DeltaPriceRange } from '#features/Prices/calculateDeltaPriceRange'
import { defaultPrintOptions } from '#features/PrintOptions/PrintOptions'
import { getCosimaDocument } from '#pages/Configuration/utils/utils'
import { type FavoritesState, makeFavoriteIdsSet } from '#store/slices/userData/favoritesSlice'
import type {
  AppDetails,
  AppProductFamily,
  AppProductGroup,
  AppProductVariant,
  FamilyProductPath,
  GroupProductPath,
  ProductPath,
  VariantProductPath,
} from '#types/AppTypes'
import { isValidDateString, stringToPeriod } from '#utils/DateString'
import { getIconForConfigurationStep } from '#utils/getIconForConfigurationStep'
import { logger } from '#utils/logger'
import type { ConfigurationOptionalEquipment, TruckConfiguration } from '../slices/configurationSlice'

export interface ConfigurationEnrichmentData {
  enrichedGroups: Partial<Record<EnhancedProductGroupId, AppProductGroup>>
  enrichedFamilies: Partial<Record<EnhancedProductFamilyId, AppProductFamily>>
  enrichedVariants: Partial<Record<`${EnhancedProductFamilyId}${EnhancedProductVariantId}`, AppProductVariant>>
  // improves type coercion: plugging a know path type gives out the correct type
  byPath: Partial<Record<GroupProductPath, AppProductGroup>> &
    Partial<Record<FamilyProductPath, AppProductFamily>> &
    Partial<Record<VariantProductPath, AppProductVariant>> &
    Partial<Record<ProductPath, AppProductGroup | AppProductFamily | AppProductVariant>>
  rootGroups: AppProductGroup[]
  deltaPriceRange: DeltaPriceRange
}

export type AppVariantCompatibility = ReturnType<typeof getCompatibilityStatus>

const log = logger('Configuration Enrichment 🗜️')

const getCompatibilityStatus = (
  family: AppProductFamily,
  variant: EnhancedProductVariant,
  configurationVersion: number | undefined,
  compatibility: TruckConfiguration['compatibility'] | undefined,
) => {
  if (variant.compatibility === 'invalid') {
    log.warn(`Variant '${family.path}${variant.id}' has a compatibility 'invalid' that should not have been there`)
  }
  // if there's no version passed, then fall back into the compatibility of the variant
  if (!configurationVersion || !compatibility) {
    return variant.compatibility
  }

  // the observed group is not part of the variants hierarchy - a good guess is that whatever the API response was, it may be invalid
  const isObserved =
    compatibility.requestedProductGroups.includes(family.parentGroup.id) || compatibility.checkedProductGroups.includes(family.parentGroup.id)
  if (!isObserved) {
    return 'not-observed' as const
  }

  // the check is more trivial, as requestedProductGroups should always include the entire group tree from the root of the check
  const isPending = compatibility.requestedProductGroups.includes(family.parentGroup.id)
  const hasBeenLoaded = compatibility.checkedProductGroups.includes(family.parentGroup.id)
  if (!isPending && !hasBeenLoaded) {
    // this is unusual, but we'll keep it for now
    log.warn(`Variant '${family.path}${variant.id}' is observed but neither is pending or has been requested, did it fall out of scope?`)
    return 'forgotten' as const
  }

  if (!hasBeenLoaded) {
    return 'pending' as const
  }

  // so far 'unknown' always meant that it has been compatible, the other states meant incompatibility
  return variant.compatibility === 'unknown' ? ('compatible' as const) : ('incompatible' as const)
}

const enrichProductVariant = (
  enrichment: ConfigurationEnrichmentData,
  variant: EnhancedProductVariant,
  family: AppProductFamily,
  configuredFamilies: TruckConfiguration['configuredFamilies'],
  initialSelection: TruckConfiguration['initialSelection'],
  favoriteIdsSet: Set<FavoriteEntryId>,
  optionalEquipments: ConfigurationOptionalEquipment[],
  tg3ConversionInsightsLookup: Tg3ConversionInsightsLookup | undefined,
  compatibility: { compatibility: TruckConfiguration['compatibility']; currentConfigurationVersion: number } | undefined,
): AppProductVariant => {
  const priceData = variant.dummy_price
  const detailData: AppDetails | undefined = undefined
  const printOptions = configuredFamilies[family.id]?.printOptions[variant.id]
  const familyInitialSelection = initialSelection?.[family.id]
  const isUserConfigured = !!(variant.isSelected && variant.id !== familyInitialSelection)
  const isPartOfOptionalEquipment = optionalEquipments
    .filter((x) => x.status !== 'removed')
    .some((o) => o.variants.some((x) => x.family.id === family.id && x.targetVariant.id === variant.id))
  const cosima = getCosimaDocument(variant, family, i18next.language.startsWith('en') ? 'en-GB' : 'de-DE')
  const isFavorite = favoriteIdsSet.has(`${family.id}${variant.id}`)
  const isSelected = variant.isSelected ?? false
  if (isFavorite && !isSelected) {
    family.hasNonSelectedFavorites = true
  }

  const compatibilityStatus = getCompatibilityStatus(family, variant, compatibility?.currentConfigurationVersion, compatibility?.compatibility)

  const insightsData = tg3ConversionInsightsLookup?.[`${family.id}${variant.id}`]

  return {
    source: 'ecm',
    printOptions: printOptions ?? defaultPrintOptions,
    ...variant,
    path: `${family.path}/${variant.id}` as VariantProductPath,
    parentFamily: family,
    priceData: priceData ? { price: priceData, deltaPrice: 0 } : undefined,
    detailData: detailData,
    insightsData: insightsData
      ? {
          tg3ConversionInsights: insightsData,
        }
      : undefined,
    compatibilityStatus,
    enrichment,
    initiallySelected: familyInitialSelection === variant.id,
    isFavorite,
    isUserConfigured,
    isSelected,
    isPartOfOptionalEquipment,
    cosima,
  }
}

const enrichProductFamily = (
  enrichment: ConfigurationEnrichmentData,
  family: EnhancedProductFamily,
  group: AppProductGroup,
  configuredFamilies: TruckConfiguration['configuredFamilies'],
  initialSelection: TruckConfiguration['initialSelection'],
  favoriteIdsSet: Set<FavoriteEntryId>,
  mode: ConfigurationMode | undefined,
  optionalEquipments: ConfigurationOptionalEquipment[],
  tg3ConversionInsightsLookup: Tg3ConversionInsightsLookup | undefined,
  compatibility: { compatibility: TruckConfiguration['compatibility']; currentConfigurationVersion: number } | undefined,
): AppProductFamily => {
  const familyDetails: AppDetails | undefined = undefined
  const isUserConfigured = configuredFamilies[family.id]?.selectedVariant !== undefined
  const isLocked = configuredFamilies[family.id]?.isLocked ?? false
  const selectedVariant = configuredFamilies[family.id]?.selectedVariant
  const isPartOfOptionalEquipment = optionalEquipments.some((x) => x.variants.some((x) => x.family.id === family.id))

  const almostEnrichedFamily: AppProductFamily = {
    source: 'ecm',
    ...family,
    path: `${group.path}/${family.id}` as FamilyProductPath,
    // variants are filled below
    variants: [],
    parentGroup: group,
    detailData: familyDetails,
    isFavorite: favoriteIdsSet.has(family.id),
    hasNonSelectedFavorites: false,
    isUserConfigured,
    isLocked,
    isPartOfOptionalEquipment,
    enrichment,
  }

  almostEnrichedFamily.variants = family.variants.map((x) =>
    enrichProductVariant(
      enrichment,
      x,
      almostEnrichedFamily,
      configuredFamilies,
      initialSelection,
      favoriteIdsSet,
      optionalEquipments,
      tg3ConversionInsightsLookup,
      compatibility,
    ),
  )

  almostEnrichedFamily.initialSelectedVariant = almostEnrichedFamily.variants.find((x) => x.initiallySelected) ?? undefined

  if (selectedVariant) {
    almostEnrichedFamily.selectedVariant = almostEnrichedFamily.variants.find((x) => x.id === selectedVariant.id)
  } else {
    almostEnrichedFamily.selectedVariant = almostEnrichedFamily.variants.find((x) => x.isSelected) ?? almostEnrichedFamily.initialSelectedVariant
  }

  const makeFamilyReadonly = mode === 'read-only'
  if (makeFamilyReadonly) {
    almostEnrichedFamily.variants = almostEnrichedFamily.variants.filter((x) => x.isSelected)
    almostEnrichedFamily.totalVariantCount = almostEnrichedFamily.variants.length
  }

  return almostEnrichedFamily
}

const enrichProductGroup = (
  enrichment: ConfigurationEnrichmentData,
  group: EnhancedProductGroup,
  parent: AppProductGroup | undefined,
  configuredFamilies: TruckConfiguration['configuredFamilies'],
  initialSelection: TruckConfiguration['initialSelection'],
  favoriteIdsSet: Set<FavoriteEntryId>,
  mode: ConfigurationMode | undefined,
  optionalEquipments: ConfigurationOptionalEquipment[],
  tg3ConversionInsightsLookup: Tg3ConversionInsightsLookup | undefined,
  compatibility: { compatibility: TruckConfiguration['compatibility']; currentConfigurationVersion: number } | undefined,
): AppProductGroup => {
  const almostEnrichedGroup: AppProductGroup = {
    source: 'ecm',
    ...group,
    parentGroup: parent,
    path: ((parent ? `${parent.path}/` : '') + encodeURIComponent(group.id)) as GroupProductPath,
    // groups and families are filled below
    subGroups: [],
    families: [],
    isFavorite: favoriteIdsSet.has(group.id),
    enrichment,
  }

  almostEnrichedGroup.subGroups =
    group.subGroups?.map((x) =>
      enrichProductGroup(
        enrichment,
        x,
        almostEnrichedGroup,
        configuredFamilies,
        initialSelection,
        favoriteIdsSet,
        mode,
        optionalEquipments,
        tg3ConversionInsightsLookup,
        compatibility,
      ),
    ) ?? []
  almostEnrichedGroup.families =
    group.families?.map((x) =>
      enrichProductFamily(
        enrichment,
        x,
        almostEnrichedGroup,
        configuredFamilies,
        initialSelection,
        favoriteIdsSet,
        mode,
        optionalEquipments,
        tg3ConversionInsightsLookup,
        compatibility,
      ),
    ) ?? []

  return almostEnrichedGroup
}

const enrichConfigurationData = (
  enrichment: ConfigurationEnrichmentData,
  step: TruckConfiguration['step']['system'],
  productGroups: TruckConfiguration['productGroups'],
  configuredFamilies: TruckConfiguration['configuredFamilies'],
  initialSelection: TruckConfiguration['initialSelection'],
  favoriteIdsSet: Set<FavoriteEntryId>,
  mode: ConfigurationMode | undefined,
  optionalEquipments: ConfigurationOptionalEquipment[],
  tg3ConversionInsightsLookup: Tg3ConversionInsightsLookup | undefined,
  compatibility: { compatibility: TruckConfiguration['compatibility']; currentConfigurationVersion: number } | undefined,
) => {
  const getChildGroups = (group: AppProductGroup): AppProductGroup[] => [group, ...group.subGroups.flatMap(getChildGroups)]
  const enrichedProductGroups = productGroups.map((x) =>
    enrichProductGroup(
      enrichment,
      x,
      undefined,
      configuredFamilies,
      initialSelection,
      favoriteIdsSet,
      mode,
      optionalEquipments,
      tg3ConversionInsightsLookup,
      compatibility,
    ),
  )

  // Assign icons to visible groups according to the step & displayed index
  const hiddenGroups = store.getState().truckConfigurations.meta.hiddenGroups
  let iconIndex = 0
  for (const group of enrichedProductGroups) {
    const isEmpty = group.families.length === 0 && group.subGroups.length === 0
    if (isEmpty) {
      continue
    }
    if (hiddenGroups?.includes(group.id)) {
      continue
    }

    group.iconUrl = getIconForConfigurationStep(step, iconIndex)
    iconIndex++
  }

  const allVisibleGroups = enrichedProductGroups.flatMap(getChildGroups)
  const allVisibleFamilies = allVisibleGroups.flatMap((x) => x.families)
  const allVisibleVariants = allVisibleFamilies.flatMap((x) => x.variants)

  // write the enriched data into the result
  for (const visibleGroup of allVisibleGroups) {
    enrichment.enrichedGroups[visibleGroup.id] = visibleGroup
    enrichment.byPath[visibleGroup.path] = visibleGroup
    if (!visibleGroup.parentGroup) {
      enrichment.rootGroups.push(visibleGroup)
    }
  }
  for (const visibleFamily of allVisibleFamilies) {
    enrichment.enrichedFamilies[visibleFamily.id] = visibleFamily
    enrichment.byPath[visibleFamily.path] = visibleFamily
  }
  for (const visibleVariant of allVisibleVariants) {
    enrichment.enrichedVariants[`${visibleVariant.parentFamily.id}${visibleVariant.id}`] = visibleVariant
    enrichment.byPath[visibleVariant.path] = visibleVariant
  }
}

// @TODO? Maybe remove this function and replace it with receptors of its various uses, instead of trying to carry so much state
export const enrichConfiguration = (
  productGroups: TruckConfiguration['productGroups'],
  configuredFamilies: TruckConfiguration['configuredFamilies'],
  initialSelection: TruckConfiguration['initialSelection'],
  marketData: TruckConfiguration['marketData'],
  favorites: FavoritesState,
  mode: ConfigurationMode | undefined,
  step: TruckConfiguration['step']['system'],
  apiConfiguration: TruckConfiguration['apiConfiguration'],
  optionalEquipments: ConfigurationOptionalEquipment[],
  tg3ConversionInsightsLookup: Tg3ConversionInsightsLookup | undefined,
  compatibility: { compatibility: TruckConfiguration['compatibility']; currentConfigurationVersion: number } | undefined,
): ConfigurationEnrichmentData => {
  const enrichment: ConfigurationEnrichmentData = {
    enrichedGroups: {},
    enrichedFamilies: {},
    enrichedVariants: {},
    byPath: {},
    rootGroups: [],
    deltaPriceRange: { minDeltaPrice: -1, maxDeltaPrice: 1, normalizationFactor: 1 },
  }
  const favoriteIdsSet = makeFavoriteIdsSet(favorites)
  enrichConfigurationData(
    enrichment,
    step,
    productGroups,
    configuredFamilies,
    initialSelection,
    favoriteIdsSet,
    mode,
    optionalEquipments,
    tg3ConversionInsightsLookup,
    compatibility,
  )

  if (step === 'DetailedStep') {
    const usedPartPeriodDate = apiConfiguration.partPeriod ?? apiConfiguration.desiredDealerDate
    const usedPartPeriod = isValidDateString(usedPartPeriodDate) ? stringToPeriod(usedPartPeriodDate) : undefined
    enrichWithNationalCodes(
      enrichment,
      marketData,
      configuredFamilies,
      favoriteIdsSet,
      mode,
      apiConfiguration.orderLanguage,
      usedPartPeriod,
      optionalEquipments,
    )
  }

  const deltaPriceRange = calculateDeltaPriceRange(Object.values(enrichment.enrichedFamilies).filter((x) => !!x))

  return {
    ...enrichment,
    deltaPriceRange,
  }
}
